import type { Project } from "../model/Project";
import type { StampLibrary } from "../stamps/StampLibrary";
import { serializeProject, deserializeProject } from "./projectFile";
import { idbGet, idbSet, idbDelete, idbKeys } from "./idbStore";
import { writeLoadHint } from "./loadHint";

const KEY_PREFIX = "leather-art:autosave:";
const INDEX_KEY = "leather-art:autosave:index";
// Short debounce so a reload / close loses at most a fraction of a second of
// work (#735). Kept well under a second per the issue. Writes now go to
// IndexedDB (async), so the teardown flush is best-effort — but anything older
// than this debounce has already been written by the time the page goes away.
const DEBOUNCE_MS = 200;

type Loaded = {
  project: Project;
  userStampMetas: ReturnType<typeof deserializeProject>["userStampMetas"];
  /** Serialized size of what was read, for the load-time hint (#1230). */
  bytes: number;
} | null;

type Backend = "idb" | "local";

/**
 * Debounced autosave to IndexedDB (#805).
 *
 * The whole project — including embedded tracing-image `data:` URLs — is
 * serialized into one record. That outgrew `localStorage`'s ~5 MB per-origin
 * cap, so storage moved to IndexedDB (much larger quota). On first run we
 * migrate any existing `localStorage` autosave into IndexedDB, and only after
 * the copy is durably committed AND read-back verified do we delete the
 * `localStorage` source — so a project is never dropped on the floor mid-move.
 * When IndexedDB is unavailable (private mode / disabled) we stay on
 * `localStorage`, exactly as before.
 *
 * Autosave is single-slot: the `index` record tracks one `lastId`, and every
 * write prunes any other project blob, so old / reset / opened-then-replaced
 * projects don't accumulate and eat quota.
 */
export class Autosave {
  private library: StampLibrary;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: Project | null = null;
  // Resolves once we've decided (and, if needed, migrated to) the backend.
  // Cached so the probe + migration run exactly once per session.
  private backend: Promise<Backend> | null = null;
  // The write in progress, if any. Writes run one after another, and a flush
  // with nothing new to write still waits for it, so `await flush()` means
  // "everything so far is stored" (#1143).
  private writing: Promise<void> = Promise.resolve();

  constructor(library: StampLibrary) {
    this.library = library;
  }

  schedule(project: Project) {
    this.pending = project;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      void this.flush();
    }, DEBOUNCE_MS);
  }

  flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!this.pending) return this.writing;
    const project = this.pending;
    this.pending = null;
    this.writing = this.writing.then(() => this.write(project));
    return this.writing;
  }

  private async write(project: Project): Promise<void> {
    let text: string;
    let index: string;
    try {
      // Compact (no indentation): autosave is a machine-only artifact, so we
      // skip pretty-printing to keep the debounced save off the main-thread
      // hot path — the embedded tracing-image data: blobs make indentation
      // needlessly expensive in both CPU and bytes (#743).
      text = serializeProject(project, this.library, false);
      index = JSON.stringify({
        lastId: project.id,
        lastModified: project.modified,
        name: project.name,
      });
    } catch (e) {
      console.warn("Autosave serialize failed", e);
      return;
    }

    if ((await this.ensureBackend()) === "idb") {
      try {
        await idbSet(KEY_PREFIX + project.id, text);
        await idbSet(INDEX_KEY, index);
        await this.pruneIdbExcept(project.id);
        this.updateHint(project, text);
      } catch (e) {
        console.warn("Autosave (IndexedDB) failed", e);
      }
    } else {
      try {
        localStorage.setItem(KEY_PREFIX + project.id, text);
        localStorage.setItem(INDEX_KEY, index);
        this.updateHint(project, text);
      } catch (e) {
        console.warn("Autosave failed", e);
      }
    }
  }

  /** Keep the load-time hint (#1230) in step with what's now stored: its
   *  size, and where the mat sits, so the next load can predict its wait and
   *  lay the mat out before the project arrives. */
  private updateHint(project: Project, text: string): void {
    writeLoadHint({ bytes: text.length, mat: project.matLayout });
  }

  /** The latest save's raw text, unparsed, or null when there is none. The
   *  app's startup restore parses it itself, so it can time the parse and
   *  let a frame through around it (#1230). */
  async readLatestText(): Promise<string | null> {
    try {
      const local = (await this.ensureBackend()) === "local";
      const get = local
        ? async (k: string) => localStorage.getItem(k) ?? undefined
        : idbGet;
      const idx = await get(INDEX_KEY);
      if (!idx) return null;
      const { lastId } = JSON.parse(idx) as { lastId: string };
      return (await get(KEY_PREFIX + lastId)) ?? null;
    } catch (e) {
      console.warn("Autosave load failed", e);
      return null;
    }
  }

  async loadLatest(): Promise<Loaded> {
    const text = await this.readLatestText();
    if (!text) return null;
    try {
      return { ...deserializeProject(text), bytes: text.length };
    } catch (e) {
      console.warn("Autosave load failed", e);
      return null;
    }
  }

  // --- backend selection + one-time migration -------------------------------

  private ensureBackend(): Promise<Backend> {
    if (!this.backend) this.backend = this.initBackend();
    return this.backend;
  }

  private async initBackend(): Promise<Backend> {
    let idbIndex: string | undefined;
    try {
      // This is also our availability probe: a throw here (absent / blocked /
      // disabled) means IndexedDB is unusable, so we stay on localStorage.
      idbIndex = await idbGet(INDEX_KEY);
    } catch {
      return "local";
    }
    try {
      if (idbIndex === undefined) {
        // IndexedDB has no autosave yet — migrate any existing localStorage one.
        await this.migrateFromLocalStorage();
      } else {
        // IndexedDB already holds the autosave; clear any localStorage remnants
        // (e.g. a migration that copied + verified but didn't finish pruning).
        this.pruneLocalStorage();
      }
    } catch (e) {
      // Migration is best-effort: on failure we keep the localStorage source
      // intact and still use IndexedDB going forward.
      console.warn("Autosave migration failed", e);
    }
    return "idb";
  }

  /** Copy the active localStorage autosave into IndexedDB, then — only after
   *  it is durably committed and read-back verified — remove the localStorage
   *  copies. Interruptible at any point without data loss: the source is never
   *  deleted before the destination is confirmed, and a re-run is idempotent. */
  private async migrateFromLocalStorage(): Promise<void> {
    let idx: string | null;
    try {
      idx = localStorage.getItem(INDEX_KEY);
    } catch {
      return; // storage unreadable — nothing to migrate
    }
    if (!idx) return;

    let lastId: string;
    try {
      lastId = (JSON.parse(idx) as { lastId: string }).lastId;
    } catch {
      return; // corrupt index — leave localStorage untouched, don't crash
    }

    let text: string | null;
    try {
      text = localStorage.getItem(KEY_PREFIX + lastId);
    } catch {
      return;
    }
    if (!text) return; // index without a blob — nothing to restore

    // Write the blob first, then the index (its presence marks migration done),
    // awaiting each COMMIT.
    await idbSet(KEY_PREFIX + lastId, text);
    await idbSet(INDEX_KEY, idx);

    // Read it back and confirm it survived the round trip BEFORE deleting the
    // source. If it didn't, throw — leaving localStorage intact for a retry.
    const check = await idbGet(KEY_PREFIX + lastId);
    if (check !== text) throw new Error("migration verify mismatch");

    // Durable + verified — now safe to reclaim the localStorage space.
    this.pruneLocalStorage();
  }

  // --- pruning --------------------------------------------------------------

  /** Remove every `leather-art:autosave:*` key from localStorage (the index
   *  key shares that prefix, so this clears the whole autosave footprint). */
  private pruneLocalStorage(): void {
    try {
      const remove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(KEY_PREFIX)) remove.push(k);
      }
      for (const k of remove) localStorage.removeItem(k);
    } catch {
      /* storage disabled — ignore */
    }
  }

  /** Drop any IndexedDB project blob that isn't the current one (single-slot).
   *  This is what deletes a reset / opened-over / previous project's autosave. */
  private async pruneIdbExcept(keepId: string): Promise<void> {
    try {
      const keys = await idbKeys();
      for (const k of keys) {
        if (k === INDEX_KEY || k === KEY_PREFIX + keepId) continue;
        if (k.startsWith(KEY_PREFIX)) await idbDelete(k);
      }
    } catch {
      /* best effort */
    }
  }
}
